import { useState, forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";
 import { Menu, X, Phone, MapPin, ShoppingCart, User, LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { useCart } from "@/contexts/CartContext";
 import { useAuth } from "@/contexts/AuthContext";
 import AuthModal from "./AuthModal";
 import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuSeparator,
   DropdownMenuTrigger,
 } from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo.webp";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Menu", path: "/menu" },
  { name: "Gallery", path: "/gallery" },
  { name: "Reservations", path: "/reservations" },
  { name: "Reviews", path: "/reviews" },
  { name: "Contact", path: "/contact" },
];

const Navbar = forwardRef<HTMLElement>((_, ref) => {
  const [isOpen, setIsOpen] = useState(false);
   const [authModalOpen, setAuthModalOpen] = useState(false);
  const location = useLocation();
  const { totalItems } = useCart();
   const { user, signOut, isLoading } = useAuth();

  return (
    <nav ref={ref} className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      {/* Top bar */}
      <div className="hidden md:block bg-muted/50 py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm text-muted-foreground">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              Main GT Road, Jhelum Cantt
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:0330-3628777" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone className="w-4 h-4" />
              0330-3628777
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Sizzlers Restaurant" className="h-10 md:h-14 w-auto" width={161} height={56} />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-foreground/80"
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Cart Icon */}
            <Link
              to="/cart"
              className="relative p-2 text-foreground/80 hover:text-primary transition-colors"
              aria-label={`Shopping cart${totalItems > 0 ? `, ${totalItems} items` : ''}`}
            >
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold bg-sizzle-orange text-white rounded-full">
                  {totalItems > 99 ? "99+" : totalItems}
                </span>
              )}
            </Link>

             {/* Auth Button / User Menu */}
             {!isLoading && (
               user ? (
                 <DropdownMenu>
                   <DropdownMenuTrigger asChild>
                     <Button variant="ghost" size="icon" className="rounded-full">
                       <User className="w-5 h-5" />
                     </Button>
                   </DropdownMenuTrigger>
                   <DropdownMenuContent align="end" className="w-48">
                     <div className="px-2 py-1.5 text-sm font-medium truncate">
                       {user.email}
                     </div>
                     <DropdownMenuSeparator />
                     <DropdownMenuItem asChild>
                       <Link to="/track-order" className="cursor-pointer">
                         My Orders
                       </Link>
                     </DropdownMenuItem>
                     <DropdownMenuSeparator />
                     <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer text-destructive">
                       <LogOut className="w-4 h-4 mr-2" />
                       Sign Out
                     </DropdownMenuItem>
                   </DropdownMenuContent>
                 </DropdownMenu>
               ) : (
                 <Button variant="outline" size="sm" onClick={() => setAuthModalOpen(true)}>
                   Sign In
                 </Button>
               )
             )}

            <Button variant="sizzle" size="sm" asChild>
              <Link to="/reservations">Book a Table</Link>
            </Button>
          </div>

          {/* Mobile - Cart and Menu button */}
          <div className="md:hidden flex items-center gap-2">
            <Link
              to="/cart"
              className="relative p-2 text-foreground/80 hover:text-primary transition-colors"
              aria-label={`Shopping cart${totalItems > 0 ? `, ${totalItems} items` : ''}`}
            >
              <ShoppingCart className="w-6 h-6" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold bg-sizzle-orange text-white rounded-full">
                  {totalItems > 99 ? "99+" : totalItems}
                </span>
              )}
            </Link>
            <button
              className="p-2 text-foreground"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-background border-t border-border">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`text-base font-medium py-2 transition-colors ${
                  location.pathname === link.path
                    ? "text-primary"
                    : "text-foreground/80"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/cart"
              onClick={() => setIsOpen(false)}
              className={`text-base font-medium py-2 transition-colors flex items-center gap-2 ${
                location.pathname === "/cart"
                  ? "text-primary"
                  : "text-foreground/80"
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              Cart {totalItems > 0 && `(${totalItems})`}
            </Link>
            <Button variant="sizzle" className="mt-2" asChild>
              <Link to="/reservations" onClick={() => setIsOpen(false)}>
                Book a Table
              </Link>
            </Button>
             
             {/* Mobile Auth */}
             {!isLoading && !user && (
               <Button 
                 variant="outline" 
                 className="mt-2" 
                 onClick={() => {
                   setIsOpen(false);
                   setAuthModalOpen(true);
                 }}
               >
                 <User className="w-4 h-4 mr-2" />
                 Sign In / Sign Up
               </Button>
             )}
             
             {!isLoading && user && (
               <div className="mt-2 pt-2 border-t border-border space-y-2">
                 <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                 <Link
                   to="/track-order"
                   onClick={() => setIsOpen(false)}
                   className="block text-base font-medium py-2 text-foreground/80"
                 >
                   My Orders
                 </Link>
                 <Button 
                   variant="ghost" 
                   size="sm" 
                   onClick={() => {
                     signOut();
                     setIsOpen(false);
                   }}
                   className="w-full justify-start text-destructive"
                 >
                   <LogOut className="w-4 h-4 mr-2" />
                   Sign Out
                 </Button>
               </div>
             )}
          </div>
        </div>
      )}
       
       <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </nav>
  );
});

Navbar.displayName = "Navbar";

export default Navbar;